import { i18n, localizePersonName } from "@dehub/theme";
import { ActionIcon, Avatar, Badge, Box, Divider, Flex, Group, Paper, Progress, Stack, Text, Tooltip } from "@mantine/core";
import {
  ArrowLeftIcon,
  BankIcon,
  CheckCircleIcon,
  ClockIcon,
  CoinsIcon,
  CreditCardIcon,
  HashIcon,
  HourglassMediumIcon,
  ReceiptIcon,
  StorefrontIcon,
  UserCircleIcon,
  UserIcon,
} from "@phosphor-icons/react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router";
import { NetworkSettlementApi } from "../../apis";
import { PageLayout } from "../../components/layout";
import { usePermission } from "../../components/permission";
import { ColumnType, DataTable } from "../../components/data-table";
import RenderText from "../../components/ui/render-text";
import { useBreadcrumb } from "../../hooks/useBreadcrumb";
import { IAuth } from "../../interfaces/IAuth";
import { IGeneral } from "../../interfaces/IGeneral";
import { IReference } from "../../interfaces/IReference";
import { currencyFormat } from "../../utils";
import { dateTimeFormat } from "../../utils/date";
import { renderBusinessName } from "../../utils/render-business-name";
import { renderFullName, renderName } from "../../utils/render-name";

function getCounterparty(state: any, myBusinessType: string | undefined) {
  const txType = state?.type;
  const useReceiver =
    (txType === "SALES" && myBusinessType === "SUPPLIER") ||
    (txType !== "SALES" && myBusinessType === "BUYER");
  return useReceiver ? state?.receiverBusiness : state?.senderBusiness;
}

type InfoChipProps = {
  icon: React.ReactNode;
  label: string;
  refCode?: string | null;
  name?: string | null;
  amount?: number | null;
  subtitle?: React.ReactNode;
  accent: "gray" | "green" | "brand";
  emphasis?: boolean;
};

function InfoChip({ icon, label, refCode, name, amount, subtitle, accent, emphasis }: InfoChipProps) {
  const hasAmount = amount != null;
  return (
    <Flex direction="column" gap={8} p="md" flex={1} miw={0}>
      <Group gap={10} wrap="nowrap" align="center">
        <Flex
          w={34}
          h={34}
          bdrs="sm"
          align="center"
          justify="center"
          bg={`${accent}.0`}
          c={`${accent}.7`}
          style={{
            boxShadow: `inset 0 0 0 1px color-mix(in srgb, var(--mantine-color-${accent}-3) 55%, transparent)`,
            flexShrink: 0,
          }}>
          {icon}
        </Flex>
        <Box miw={0} style={{ flex: 1 }}>
          <Text fz={14} fw={700} c="gray.7" tt="uppercase" lts={0.8} lh={1.1}>
            {label}
          </Text>
          {hasAmount ? (
            <Text
              fz={emphasis ? 16 : 14}
              fw={700}
              c={`${accent}.7`}
              lh={1.3}
              mt={2}
              style={{ fontVariantNumeric: "tabular-nums", letterSpacing: "-0.01em" }}>
              {currencyFormat(amount ?? 0)}
            </Text>
          ) : (
            <Group gap={6} mt={2} wrap="nowrap">
              {refCode && (
                <Badge
                  size="xs"
                  radius="sm"
                  variant="light"
                  color={accent}
                  ff="monospace"
                  styles={{ root: { letterSpacing: 0.3 } }}>
                  #{refCode}
                </Badge>
              )}
              <Text fz="sm" fw={500} c="gray.9" truncate>
                {name || "-"}
              </Text>
            </Group>
          )}
          {subtitle && (
            <Text fz={14} c="dimmed" lh={1.3} mt={2} truncate>
              {subtitle}
            </Text>
          )}
        </Box>
      </Group>
    </Flex>
  );
}

export const BillingHistory = () => {
  const { t } = useTranslation("billing");
  const paymentMethod = useSelector((state: { general: IGeneral }) => state.general.paymentMethod);
  const myBusinessType = useSelector((state: { auth: IAuth }) => state.auth.myBusinessType);
  const INV_SETTLEMENT_HISTORY = usePermission("INV_SETTLEMENT_HISTORY");
  const params = useParams();
  const [filters] = useState<any>({ invoiceId: params.id! });
  const breadcrumbs = useBreadcrumb("billing", [
    { to: "/", key: "breadcrumb.home" },
    { key: "breadcrumb.invoiceManagement" },
    { key: "breadcrumb.customerBilling" },
    { key: "breadcrumb.details" },
    { key: "breadcrumb.paymentHistory" },
  ]);
  const navigate = useNavigate();
  const location = useLocation();
  const { state = {} } = location;

  const counterparty = useMemo(() => getCounterparty(state, myBusinessType), [state, myBusinessType]);
  const partnerInfo = counterparty?.partner
    ? { refCode: counterparty.partner?.refCode ?? null, name: renderBusinessName(counterparty.partner, "") || null }
    : null;
  const businessInfo = counterparty
    ? { refCode: counterparty?.refCode ?? null, name: renderBusinessName(counterparty, "") || null }
    : null;

  const confirmedAmount = Number(state?.confirmedAmount ?? 0);
  const remainingAmount = Number(state?.amountToPay ?? 0);
  const paidAmount = Math.max(0, confirmedAmount - remainingAmount);
  const paidPercent =
    confirmedAmount > 0 ? Math.min(100, Math.round((paidAmount / confirmedAmount) * 100)) : 0;

  const columns = useHeader({ paymentMethod, state });

  return (
    <PageLayout
      title={
        <Flex align="center" gap="sm" wrap="wrap">
          <ActionIcon
            onClick={() => navigate(-1)}
            variant="subtle"
            color="gray"
            size="md"
            radius="md"
            aria-label={t("back")}>
            <ArrowLeftIcon size={16} />
          </ActionIcon>
          <Box>
            <Group gap={6} mb={2}>
              <ReceiptIcon size={12} color="var(--mantine-color-brand-6)" />
              <Text fz={14} fw={700} c="gray.9" tt="uppercase" lts={1.2} lh={1}>
                {t("history.title")}
              </Text>
            </Group>
            <Group gap={6} align="baseline">
              <Text fw={700} fz={18} c="gray.9" lh={1.2}>
                #
              </Text>
              <Text
                fw={700}
                fz={18}
                c="gray.9"
                ff="monospace"
                lh={1.2}
                style={{ letterSpacing: "-0.01em" }}>
                {state?.refCode || "-"}
              </Text>
            </Group>
          </Box>
        </Flex>
      }
      breadcrumb={breadcrumbs}>
      {INV_SETTLEMENT_HISTORY.hasAccess ? (
        <Stack gap="md">
          <Paper
            radius="md"
            withBorder
            p={0}
            styles={{
              root: {
                overflow: "hidden",
                borderColor: "var(--mantine-color-gray-2)",
                background:
                  "linear-gradient(180deg, color-mix(in srgb, var(--mantine-color-brand-0) 40%, var(--mantine-color-white)), var(--mantine-color-white))",
              },
            }}>
            <Flex align="stretch" gap={0} wrap="wrap">
              <Box style={{ flex: "1 1 220px", minWidth: 0 }}>
                <InfoChip
                  icon={<UserCircleIcon size={18} weight="duotone" />}
                  label={t("history.partnerName")}
                  refCode={partnerInfo?.refCode ?? null}
                  name={partnerInfo?.name ?? null}
                  accent="gray"
                />
              </Box>
              <Divider orientation="vertical" color="gray.2" />
              <Box style={{ flex: "1 1 220px", minWidth: 0 }}>
                <InfoChip
                  icon={<StorefrontIcon size={18} weight="duotone" />}
                  label={myBusinessType === "SUPPLIER" ? t("col.buyerBusiness") : t("col.supplierBusiness")}
                  refCode={businessInfo?.refCode ?? null}
                  name={businessInfo?.name ?? null}
                  accent="gray"
                />
              </Box>
              <Divider orientation="vertical" color="gray.2" />
              <Box style={{ flex: "1 1 220px", minWidth: 0 }}>
                <InfoChip
                  icon={<CheckCircleIcon size={18} weight="duotone" />}
                  label={t("history.confirmed")}
                  amount={confirmedAmount}
                  subtitle={state?.confirmedDate ? dateTimeFormat(`${state.confirmedDate}`) : null}
                  accent="green"
                />
              </Box>
              <Divider orientation="vertical" color="gray.2" />
              <Box style={{ flex: "1 1 220px", minWidth: 0 }}>
                <InfoChip
                  icon={<HourglassMediumIcon size={18} weight="duotone" />}
                  label={t("history.remainingAmount")}
                  amount={remainingAmount}
                  subtitle={
                    confirmedAmount > 0 ? t("paidPercent", { percent: paidPercent }) : null
                  }
                  accent="brand"
                  emphasis
                />
              </Box>
            </Flex>

            <Box
              px="md"
              py="sm"
              style={{
                borderTop: "1px dashed var(--mantine-color-gray-3)",
                background: "color-mix(in srgb, var(--mantine-color-gray-0) 80%, transparent)",
              }}>
              <Group gap="sm" wrap="nowrap" mb={6}>
                <Text fz={14} fw={700} c="gray.8" tt="uppercase" lts={1}>
                  {t("paymentProgress")}
                </Text>
                <Text
                  fz={14}
                  fw={700}
                  c="gray.9"
                  style={{ fontVariantNumeric: "tabular-nums" }}>
                  {paidPercent}%
                </Text>
                <Box style={{ flex: 1 }} />
                <Text
                  fz={14}
                  c="dimmed"
                  style={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                  {currencyFormat(paidAmount)} / {currencyFormat(confirmedAmount)}
                </Text>
              </Group>
              <Progress
                value={paidPercent}
                size="sm"
                radius="xl"
                color="brand"
                styles={{
                  root: { background: "var(--mantine-color-gray-2)" },
                }}
              />
            </Box>
          </Paper>

          <Paper
            radius="md"
            withBorder
            p="md"
            styles={{ root: { borderColor: "var(--mantine-color-gray-2)" } }}>
            <Group justify="space-between" mb="sm" wrap="nowrap">
              <Group gap={8} wrap="nowrap">
                <Flex
                  w={28}
                  h={28}
                  bdrs="sm"
                  align="center"
                  justify="center"
                  bg="brand.0"
                  c="gray.9">
                  <ReceiptIcon size={14} weight="fill" />
                </Flex>
                <Box>
                  <Text fz={14} fw={700} c="gray.7" tt="uppercase" lts={1} lh={1}>
                    {t("transactionLog")}
                  </Text>
                  <Text fz="sm" fw={600} c="gray.9" lh={1.3}>
                    {t("transactionLogSub")}
                  </Text>
                </Box>
              </Group>
            </Group>
            {params?.id && (
              <DataTable
                name="requests.list.history"
                columns={columns}
                filters={filters}
                loadData={(data) => NetworkSettlementApi.history(data!)}
                striped="odd"
              />
            )}
          </Paper>
        </Stack>
      ) : (
        INV_SETTLEMENT_HISTORY.accessDenied()
      )}
    </PageLayout>
  );
};

const ColHeader = ({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) => (
  <Group gap={6} wrap="nowrap">
    <Box c="gray.7" display="flex">
      {icon}
    </Box>
    <Text fz={14} fw={700} c="gray.8" tt="uppercase" lts={0.4}>
      {children}
    </Text>
  </Group>
);

const useHeader = ({ paymentMethod, state }: { paymentMethod: IReference[]; state: any }): ColumnType<any>[] => {
  const { t } = useTranslation("billing");
  return [
    {
      title: "#",
      render: (_r, index) => (
        <Text fz="sm" c="dimmed" fw={500} style={{ fontVariantNumeric: "tabular-nums" }}>
          {index + 1}
        </Text>
      ),
      width: 40,
    },
    {
      title: <ColHeader icon={<ClockIcon size={12} />}>{t("col.txDate")}</ColHeader>,
      render: (record) => (
        <Text fz="sm" c="gray.9" style={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
          {dateTimeFormat(record.createdAt)}
        </Text>
      ),
    },
    {
      title: <ColHeader icon={<HashIcon size={12} />}>{t("col.txRef")}</ColHeader>,
      render: (record) =>
        record?.trxRefCode ? (
          <Badge
            size="sm"
            radius="sm"
            variant="light"
            color="gray"
            ff="monospace"
            styles={{ root: { letterSpacing: 0.3, textTransform: "none" } }}>
            {record.trxRefCode}
          </Badge>
        ) : (
          <Text fz="sm" c="dimmed">
            -
          </Text>
        ),
    },
    {
      title: <ColHeader icon={<ReceiptIcon size={12} />}>{t("col.invoiceRef")}</ColHeader>,
      render: () =>
        state?.refCode ? (
          <Badge
            size="sm"
            radius="sm"
            variant="light"
            color="brand"
            ff="monospace"
            styles={{ root: { letterSpacing: 0.3, textTransform: "none" } }}>
            #{state.refCode}
          </Badge>
        ) : (
          <Text fz="sm" c="dimmed">
            -
          </Text>
        ),
    },
    {
      title: <ColHeader icon={<CoinsIcon size={12} />}>{t("col.txAmount")}</ColHeader>,
      align: "right",
      render: (record) => (
        <Text
          fz="sm"
          fw={700}
          c="gray.8"
          ta="right"
          style={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap", letterSpacing: "-0.01em" }}>
          {currencyFormat(record?.trxAmount ?? 0)}
        </Text>
      ),
    },
    {
      title: <ColHeader icon={<CreditCardIcon size={12} />}>{t("col.paymentMethod")}</ColHeader>,
      render: (record) => {
        const name = paymentMethod.find((i: IReference) => i.code === record.paymentMethod)?.name ?? null;
        if (!name) return <RenderText text="-" />;
        return (
          <Badge
            size="sm"
            radius="sm"
            variant="light"
            color="gray"
            styles={{ root: { textTransform: "none" } }}>
            {name}
          </Badge>
        );
      },
    },
    {
      title: <ColHeader icon={<BankIcon size={12} />}>{t("col.receiverAcc")}</ColHeader>,
      render: (record) =>
        record?.debitAcc ? (
          <Box miw={0} style={{ maxWidth: 240 }}>
            <Text fz="sm" fw={500} c="gray.9" lh={1.3} truncate>
              {record.debitAcc?.name ?? "-"}
            </Text>
            <Group gap={6} wrap="nowrap" mt={1}>
              <Text
                fz={14}
                c="dimmed"
                style={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                {record.debitAcc?.number ?? "-"}
              </Text>
              {record.debitAcc?.bankName && (
                <>
                  <Text fz={14} c="dimmed">
                    ·
                  </Text>
                  <Text fz={14} c="dimmed" truncate>
                    {record.debitAcc.bankName}
                  </Text>
                </>
              )}
            </Group>
          </Box>
        ) : (
          <Text fz="sm" c="dimmed">
            -
          </Text>
        ),
    },
    {
      title: <ColHeader icon={<BankIcon size={12} />}>{t("col.senderAcc")}</ColHeader>,
      render: (record) =>
        record?.creditAcc ? (
          <Box miw={0} style={{ maxWidth: 240 }}>
            <Text fz="sm" fw={500} c="gray.9" lh={1.3} truncate>
              {record.creditAcc?.name ?? "-"}
            </Text>
            <Group gap={6} wrap="nowrap" mt={1}>
              <Text
                fz={14}
                c="dimmed"
                style={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                {record.creditAcc?.number ?? "-"}
              </Text>
              {record.creditAcc?.bankName && (
                <>
                  <Text fz={14} c="dimmed">
                    ·
                  </Text>
                  <Text fz={14} c="dimmed" truncate>
                    {record.creditAcc.bankName}
                  </Text>
                </>
              )}
            </Group>
          </Box>
        ) : (
          <Text fz="sm" c="dimmed">
            -
          </Text>
        ),
    },
    {
      title: <ColHeader icon={<UserIcon size={12} />}>{t("col.paidBy")}</ColHeader>,
      render: (record) =>
        record.actionUser ? (
          <Tooltip
            label={renderFullName(record.actionUser)}
            withArrow
            position="left">
            <Group gap={8} wrap="nowrap">
              <Avatar
                size={28}
                radius="xl"
                src={record.actionUser?.avatar}
                color="brand"
                styles={{
                  root: { border: "1.5px solid var(--mantine-color-gray-2)" },
                }}>
                {(localizePersonName(record.actionUser, i18n.language).firstName || "?").slice(0, 1).toUpperCase()}
              </Avatar>
              <Box miw={0}>
                <Text fz="sm" fw={600} c="gray.9" lh={1.2} truncate>
                  {renderName(record.actionUser)}
                </Text>
              </Box>
            </Group>
          </Tooltip>
        ) : (
          <Text fz="sm" c="dimmed">
            -
          </Text>
        ),
    },
  ];
};
